const GENERATION_MESSAGES = [
  'Wir tauchen den Pinsel in die Farbe …',
  'Die Leinwand wartet gespannt …',
  'Dein Tier wird zur Kunstikone …',
  'Wir mischen Umbra, Ocker und ein bisschen Magie …',
  'Die Schnurrhaare kriegen besondere Aufmerksamkeit …',
  'Etwas Glanz für das Fell, bitte …',
  'Wir stehlen ein paar Tricks von Rembrandt …',
  'Pinsel in Hand, Muse im Anflug …',
  'Das Porträt nimmt langsam Form an …',
  'Jeder Strich zählt – wir nehmen uns Zeit …',
  'Dein Haustier ahnt schon, dass etwas Besonderes entsteht …',
  'Die Leinwand atmet Farbe …',
  'Wir suchen den perfekten Ton für diese Augen …',
  'Ein Meisterwerk braucht manchmal einen Moment …',
  'Wir verleihen dem Fell Tiefe und Charakter …',
  'Der Rahmen wird schon poliert …',
  'Dein Tier sitzt Modell – (fast) geduldig …',
  'Wir setzen den letzten Glanzpunkt …',
  'Noch ein bisschen Weiß für die Highlights …',
  'Wir geben alles für ein porträtiertes Prachtexemplar …',
];

const NEWSLETTER_KEY = 'kunstpfote_newsletter_signed_up';

const PROGRESS_DURATION = 35_000;
const POLL_INTERVAL = 1_000;
const MSG_INTERVAL = 3_800;

class GenerationProgressPanel extends HTMLElement {
  connectedCallback() {
    this._barFill = this.querySelector('.gen-modal__bar-fill');
    this._barTrack = this.querySelector('.gen-modal__bar');
    this._percentEl = this.querySelector('.gen-modal__percent');
    this._messageEl = this.querySelector('.gen-modal__message');
    this._resultEl = this.querySelector('.gen-modal__result');
    this._resultImg = this.querySelector('.gen-modal__result-img');
    this._errorEl = this.querySelector('.gen-modal__error');
    this._ctaBtn = this.querySelector('.gen-modal__cta');
    this._titleEl = this.closest('dialog-component')?.querySelector('.gen-modal__title');
    this._titleText = this._titleEl?.textContent;

    this._ctaBtn?.addEventListener('click', () => this._goToOrder());

    this._newsletterEl = this.querySelector('.gen-modal__newsletter');
    this._setupNewsletter();

    // Clean up when the dialog closes
    this.closest('dialog-component')
      ?.querySelector('dialog[ref="dialog"]')
      ?.addEventListener('close', () => this._cleanup());

    window.addEventListener('kunstpfote:generation-started', (e) => {
      this._begin(e.detail);
    });
  }

  async _begin({ sessionId, sku, uploadBase, productUrl, name }) {
    // Während auf /generate gewartet wird, keinen zweiten Start zulassen (Doppelklick)
    if (this._starting) return;
    this._starting = true;
    this._done = false;
    this._sessionId = sessionId;
    this._sku = sku;
    this._uploadBase = uploadBase;
    this._productUrl = productUrl || '';
    this._imageUrl = `${uploadBase}/image/${sessionId}/generated/${sku}.jpg`;

    // Reset UI
    this.classList.remove('gen-modal__panel--done', 'gen-modal__panel--blocked');
    this.classList.add('gen-modal__panel--starting');
    if (this._titleEl) this._titleEl.textContent = this._titleText;
    if (this._ctaBtn) this._ctaBtn.hidden = true;
    if (this._newsletterEl) this._newsletterEl.hidden = this._newsletterDone();
    this._resultEl.hidden = true;
    this._resultImg.src = '';
    this._errorEl.hidden = true;
    this._barFill.style.transition = '';
    this._setProgress(0);
    this._messageEl.classList.remove('gen-modal__message--visible');

    // Open the dialog, then lock it
    this.closest('dialog-component')?.showDialog();
    this._lockModal();

    // Call /generate and wait briefly for the answer: a blocked session (403) answers
    // instantly and must never see the progress bar. Max. 1.5 s, then start regardless.
    const call = this._callGenerate(sessionId, sku, uploadBase, name);
    await Promise.race([call, new Promise((r) => setTimeout(r, 1_500))]);
    this._starting = false;
    this.classList.remove('gen-modal__panel--starting');
    if (this._done) return; // gesperrt (403) oder Modal inzwischen geschlossen

    // Fake progress: 0 → 90% over 35 s
    this._startProgress();

    // Rotating witty messages
    this._startMessages();

    // Poll every second for the generated image
    this._startPolling();
  }

  async _callGenerate(sessionId, sku, uploadBase, name) {
    try {
      const body = { sessionId };
      if (sku) body.motiv = sku;
      if (name) body.name = name;

      const res = await fetch(`${uploadBase}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const msg = res.status === 503
          ? 'Server gerade beschäftigt – wir versuchen es weiter …'
          : json.error || `Fehler beim Starten (${res.status})`;
        if (!this._done) {
          this._errorEl.textContent = msg;
          this._errorEl.hidden = false;
        }
        // 403 = Session im Admin gesperrt → nicht weiter pollen, Modal freigeben
        if (res.status === 403) {
          this.classList.add('gen-modal__panel--blocked');
          this._cleanup();
        }
      }
    } catch {
      // Network error – polling continues, server may have queued the job
    }
  }

  _startProgress() {
    const t0 = performance.now();
    const tick = () => {
      if (this._done) return;
      const pct = Math.min(((performance.now() - t0) / PROGRESS_DURATION) * 90, 90);
      this._setProgress(pct);
      if (pct < 90) this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _startMessages() {
    const msgs = [...GENERATION_MESSAGES].sort(() => Math.random() - 0.5);
    let idx = 0;

    const show = () => {
      if (this._done) return;
      this._messageEl.classList.remove('gen-modal__message--visible');
      setTimeout(() => {
        if (this._done) return;
        this._messageEl.textContent = msgs[idx % msgs.length];
        this._messageEl.classList.add('gen-modal__message--visible');
        idx++;
      }, 350);
    };

    show();
    this._msgTimer = setInterval(show, MSG_INTERVAL);
  }

  _startPolling() {
    this._pollTimer = setInterval(() => {
      if (this._done) return;
      const img = new Image();
      img.onload = () => { if (!this._done) this._onImageReady(); };
      img.src = `${this._imageUrl}?_t=${Date.now()}`;
    }, POLL_INTERVAL);
  }

  _lockModal() {
    const dc = this.closest('dialog-component');
    this._closeBtn = dc?.querySelector('.gen-modal__close');
    this._lockedDialog = dc?.querySelector('dialog[ref="dialog"]');

    if (this._closeBtn) this._closeBtn.hidden = true;

    this._escapeGuard = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    this._lockedDialog?.addEventListener('keydown', this._escapeGuard, { capture: true });
  }

  _unlockModal() {
    if (this._closeBtn) this._closeBtn.hidden = false;
    if (this._escapeGuard && this._lockedDialog) {
      this._lockedDialog.removeEventListener('keydown', this._escapeGuard, { capture: true });
    }
    this._closeBtn = null;
    this._escapeGuard = null;
    this._lockedDialog = null;
  }

  _onImageReady() {
    this._done = true;
    clearInterval(this._pollTimer);
    clearInterval(this._msgTimer);
    cancelAnimationFrame(this._rafId);
    this._unlockModal();

    // Persist SKU in localStorage (guard against missing SKU)
    const sku = this._sku;
    if (sku) {
      const UPLOAD_DATA_KEY = 'kunstpfote_uploads';
      try {
        const raw = localStorage.getItem(UPLOAD_DATA_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          data.generated = data.generated ?? [];
          const alreadyStored = data.generated.some((e) => {
            const s = typeof e === 'string' ? e.replace(/\.jpg$/i, '') : e?.sku;
            return s === sku;
          });
          if (!alreadyStored) {
            const entry = { sku };
            if (this._productUrl) entry.url = this._productUrl;
            data.generated.push(entry);
          }
          localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));
        }
      } catch {}
    }

    // Notify PDP block and nav modal
    window.dispatchEvent(new CustomEvent('kunstpfote:generation-complete', {
      detail: { sku, sessionId: this._sessionId, uploadBase: this._uploadBase },
    }));

    // Reveal image – progress bar and message give way to the result
    this._resultImg.onload = () => {
      this.classList.add('gen-modal__panel--done');
      if (this._titleEl) this._titleEl.textContent = 'Dein Kunstporträt ist fertig';
      // CTA only where there is an order section to jump to (PDP)
      if (this._ctaBtn) this._ctaBtn.hidden = !document.querySelector('.pf-order');
      this._resultEl.hidden = false;
    };
    this._resultImg.src = this._imageUrl;
  }

  _newsletterDone() {
    try {
      return localStorage.getItem(NEWSLETTER_KEY) === '1';
    } catch {
      return false;
    }
  }

  // The form posts into a hidden iframe instead of the page: a normal submit would reload the
  // page and abort the generation, and fetch() can't pass Shopify's hCaptcha (its script
  // intercepts the submit, adds a token and submits natively – which then lands in the iframe).
  // Shopify's native double opt-in stays in charge. Any page landing in the iframe counts as sent,
  // only a captcha challenge counts as failure – the email format is already checked by the browser.
  _setupNewsletter() {
    const form = this._newsletterEl?.querySelector('form');
    const frame = this._newsletterEl?.querySelector('iframe');
    if (!form || !frame) return;

    form.target = frame.name;
    const button = form.querySelector('button[type="submit"]');
    const errorEl = form.querySelector('.gen-modal__newsletter-error');
    let submitted = false;
    let timeout;

    const fail = () => {
      clearTimeout(timeout);
      errorEl.textContent = 'Das hat leider nicht geklappt – bitte prüf deine E-Mail-Adresse und versuch es nochmal.';
      errorEl.hidden = false;
      button.disabled = false;
    };

    form.addEventListener('submit', () => {
      submitted = true;
      errorEl.hidden = true;
      button.disabled = true;
      clearTimeout(timeout);
      timeout = setTimeout(fail, 20_000);
    });

    frame.addEventListener('load', () => {
      if (!submitted) return;
      let url = '';
      try {
        url = frame.contentWindow.location.href;
      } catch {
        // Unreadable (e.g. cross-origin redirect) – the post went through
      }
      if (url === 'about:blank') return;
      if (url.includes('/challenge')) return fail();

      clearTimeout(timeout);
      errorEl.hidden = true;
      try {
        localStorage.setItem(NEWSLETTER_KEY, '1');
      } catch {}
      form.hidden = true;
      this._newsletterEl.querySelector('.gen-modal__newsletter-success').hidden = false;
    });
  }

  async _goToOrder() {
    // Scroll only after close – closing restores the scroll position locked by the dialog
    await this.closest('dialog-component')?.closeDialog();
    // Title + price, not the order section itself – on mobile price and ATC button still fit on screen
    document.querySelector('.product-title-price')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _setProgress(pct) {
    const v = Math.round(pct);
    this._barFill.style.width = `${pct}%`;
    this._barTrack?.setAttribute('aria-valuenow', v);
    this._percentEl.textContent = `${v} %`;
  }

  _cleanup() {
    this._done = true;
    clearInterval(this._pollTimer);
    clearInterval(this._msgTimer);
    cancelAnimationFrame(this._rafId);
    this._unlockModal();
  }
}

customElements.define('generation-progress-panel', GenerationProgressPanel);
